import type { FC, PropsWithChildren } from 'react';
import { useCallback, useEffect } from 'react';

import { Col, Text, useMedia } from '@opendoor/bricks-next';
import { Link } from 'platform/bricks-next';
import { AuthNavBarWidget } from 'platform/components/AuthNavBarWidget';
import { FullPageLayout, LayoutContent } from 'platform/components/layout/Layout';
import { LogoLink } from 'platform/components/OpendoorLogo';
import { useTOSContext } from 'platform/components/TOSProvider';
import { useObservability } from 'platform/observability';
import { NavBar } from 'shared/components/NavBar';
import { CXA_EMAIL_NEW as CXA_EMAIL } from 'shared/helpers/globals';
import { ErrorImage } from 'shared/helpers/svgCollection';
import { normalizePathname, searchParamsToObject } from 'shared/hooks/usePath';

type ErrorCause = 'user-error' | 'system-error';

type HubGenericErrorPageProps = PropsWithChildren<{
  reason: string;
  // Is the error expected and caused by the user (e.g. unauthenticated or not
  // authorized), or is it unexpected and caused by a bug or system issue (e.g.
  // an outage)? This is used for metrics purposes.
  causedBy: ErrorCause;
  error?: Error;
  headerContent?: string;
}>;
/**
 * Generic Error Page with automatic tracking
 */
export const GenericErrorPage: FC<HubGenericErrorPageProps> = ({
  reason,
  causedBy,
  headerContent = 'Oops, something went wrong',
  children,
}) => {
  const { trackGenericErrorPageView } = useTrackGenericErrorPageViewEvent();

  useEffect(
    () => trackGenericErrorPageView(reason, causedBy),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [],
  );

  return <ErrorPageContent headerContent={headerContent}>{children}</ErrorPageContent>;
};

export const ErrorPageContent: FC<Pick<HubGenericErrorPageProps, 'headerContent' | 'children'>> = ({
  headerContent = 'Oops, something went wrong',
  children,
}) => {
  const { smallerThanMD } = useMedia();
  const { forceHideTOSModal } = useTOSContext();

  // DO NOT REMOVE: Do not show the TOS modal on this page
  useEffect(() => {
    // set this as false on the initial render - so it doesn't show it on this page
    forceHideTOSModal(true);
    return () => {
      // allow the TOS component to be shown when we unmount
      forceHideTOSModal(false);
    };
  }, [forceHideTOSModal]);

  return (
    <FullPageLayout>
      <NavBar
        leftComponent={<LogoLink variant="primary" height="$24x" />}
        rightComponent={<AuthNavBarWidget />}
      />
      <LayoutContent>
        <Col alignItems="center" data-testid="generic-error-page">
          <Col mt="$16x" mb="$4x">
            <ErrorImage width={smallerThanMD ? 120 : 180} />
          </Col>
          <Text
            tag="p"
            mt="$10x"
            mb="$8x"
            typography="$subheaderSmall"
            textAlign="center"
            $largerThanSM={{ typography: '$headerLarge' }}
          >
            {headerContent}
          </Text>
          {children ?? (
            <Text tag="p" typography="$bodyMedium" textAlign="center">
              It looks like we ran into an issue. Please try again.
              <br />
              Email us at{' '}
              <Link
                href={`mailto:${CXA_EMAIL}`}
                analyticsName="cake-generic-error-page-email-opendoor"
              >
                {CXA_EMAIL}
              </Link>{' '}
              if the problem persists.
            </Text>
          )}
        </Col>
      </LayoutContent>
    </FullPageLayout>
  );
};

/**
 * Redirect to generic error route with metrics
 */
export const useTrackGenericErrorPageViewEvent = () => {
  const { trackEvent } = useObservability();
  const location = window.location;
  const normalizedPath = normalizePathname(location.pathname);

  const trackGenericErrorPageView = useCallback(
    (reason: string, causedBy: ErrorCause, data?: Record<string, unknown>) => {
      trackEvent(
        'error-page-view',
        normalizedPath,
        {
          ...data,
          team: data?.team ?? 'seller',
          reason,
          causedBy,
          path: normalizedPath,
          pathname: location.pathname,
          hash: location.hash,
          queryParams: searchParamsToObject(location.search),
        },
        { addTiming: true },
      );
    },
    // Don't put trackEvent in deps array here because the contextual data could change often.
    // Don't put location here because we already have normalizedPath.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recompute on explicit dependencies
    [normalizedPath],
  );

  return { trackGenericErrorPageView };
};
